/**
 * Client mirror of the server's report rule, so the UI never offers a report action the
 * server refuses. Server side: REPORT_TYPES in server/constants/reports.js and
 * canAccessReportType() / reportTypesWithin() in server/middleware/policy.js.
 *
 * Every report type carries pay, so listing, reading, exporting, updating or deleting a
 * report needs the reports verb AND salaries:read_all. The Meezan transfer sheet (account
 * numbers and salaries) always needs salaries:read_all.
 *
 * Each helper takes AuthContext's `hasPermission(resource, action)`.
 */

const readsAllPay = (hasPermission) => hasPermission('salaries', 'read_all');

/**
 * Whether any report is reachable at all: the History list and a report's detail.
 * ponytail: assumes every type is pay-bearing, as the server's REPORT_TYPES does today. If a
 * pay-free type is ever added, a reports:read holder without salaries:read_all can reach it;
 * read that from GET /api/reports/types instead.
 */
export const canReachReports = (hasPermission) =>
  hasPermission('reports', 'read') && readsAllPay(hasPermission);

/**
 * The Salary Sheet generator. Besides the POST (reports:create) it lists and reads the
 * month's existing sheet (GET /api/reports, reports:read) to offer an overwrite, so it
 * needs both.
 */
export const canCreateSalarySheet = (hasPermission) =>
  hasPermission('reports', 'create') && canReachReports(hasPermission);

/**
 * Overwriting a month's existing salary sheet: PUT /api/reports/:id needs reports:update, a
 * grant reports:create does not imply (the seeded finance_manager holds create but not update).
 */
export const canUpdateSalarySheet = (hasPermission) =>
  hasPermission('reports', 'update') && canReachReports(hasPermission);

/**
 * The Meezan transfer sheet: the export takes reports:read or reports:export plus
 * salaries:read_all, and the page first lists salary sheets (GET /api/reports,
 * reports:read) to pick the one to export, so reports:read is the grant that works.
 */
export const canExportMeezan = (hasPermission) => canReachReports(hasPermission);

/** The employee roster export on the New Report page (GET /api/employees/export). */
export const canExportEmployees = (hasPermission) =>
  hasPermission('employees', 'read_all') ||
  hasPermission('employees', 'read_department') ||
  hasPermission('employees', 'read_own');

/** Whether the New Report page has at least one card for this caller. */
export const canStartAnyReport = (hasPermission) =>
  canCreateSalarySheet(hasPermission) || canExportMeezan(hasPermission) || canExportEmployees(hasPermission);
